import { normalizeZodSafeParseResult } from '@salescore/buff-common'
import {
  type CoreModel,
  presetFunctions,
  type ViewConfigKpi,
  viewConfigKpiSchema,
  type ViewConfigSheet,
  type ViewConfigTreeNode,
} from '@salescore/core'
import { Array, pipe } from 'effect'
import { t } from 'i18next'
import { useMemo, useReducer } from 'react'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import type { ZodSafeParseResult } from 'zod'

import {
  findSameReferenceToChild,
  type ReferenceToPropertyForAddChildNodeMutation,
} from '../../../components/view_ui/ViewUISheet/findSameReferenceToChild'
import { kpiFormAtom, viewsContextAtom } from '../atoms'
import { useCurrentNodeModels, useCurrentSheet } from '../selectors/kpiFormSelector'
import { useModelSearcher } from '../selectors/modelSearcherSelector'
import { metricFormValueToViewConfigKpiPatch, updateMetricFormValue } from '../util'

export const useSetSheetConfig = (): ((sheetConfig: ViewConfigSheet) => void) => {
  const setKpiFormAtom = useSetRecoilState(kpiFormAtom)

  return (sheetConfig: ViewConfigSheet) => {
    setKpiFormAtom((oldValue) => updateMetricFormValue(oldValue, { sheet: sheetConfig }))
  }
}

export type KpiFormStep = 0 | 1 | 2 | 3
export const MAX_STEP = 3

const isKpiFormStep = (value: number): value is KpiFormStep => Array.range(0, 3).includes(value)
const stepReducer = (state: KpiFormStep, moveTo: number): KpiFormStep => (isKpiFormStep(moveTo) ? moveTo : state)
export const useStepState = (initialStep: KpiFormStep) => useReducer(stepReducer, initialStep)

export const useStepStatus = (): {
  checkStepEnabled: (step: number) => boolean
  saveEnabled: boolean
  /** 入力エラーとなっている項目 */
  errorItems: Array<{ step: number; label: string }>
} => {
  const form = useRecoilValue(kpiFormAtom)
  const { kpiConfig } = useValidatedFormValue()
  const errorItems: Array<{ step: number; label: string }> = []
  if (!(typeof form.name === 'string' && form.name.length > 0)) {
    errorItems.push({ step: 0, label: t('①KPI - KPI名') })
  }
  const sheet = form.metricFormValue?.sheet
  const measure = form.metricFormValue?.measure
  const isSelectedSheet = sheet?.type === 'sheet' && sheet.tree !== undefined
  if (!isSelectedSheet) {
    errorItems.push({ step: 1, label: t('②オブジェクト - 集計のベースとなるオブジェクト') })
  }
  if (!(isSelectedSheet && measure !== undefined)) {
    errorItems.push({ step: 2, label: t('③条件設定 - 集計方法') })
  }
  if (kpiConfig.error?.issues.some((x) => x.path.includes('measure')) ?? false) {
    errorItems.push({
      step: 2,
      label: measure?.type === 'kpiPreset' ? t('③条件設定 - 集計項目') : t('③条件設定 - SQL'),
    })
  }
  return {
    // 引数で指定したstepより前のstepでバリデーションでエラーが無いなら編集を有効にする
    checkStepEnabled: (step: number) => !errorItems.some((x) => x.step < step),
    saveEnabled: errorItems.length === 0 && kpiConfig.success,
    errorItems,
  }
}

export const useChildModelsSelectOptions = (
  nodes: Array<{ node: ViewConfigTreeNode; model: CoreModel; nodePathAsLabel: string[] }>,
) => {
  const modelSearcher = useModelSearcher()
  const { models } = useRecoilValue(viewsContextAtom)
  return useMemo(
    () =>
      nodes.flatMap(({ node, model, nodePathAsLabel }) => {
        //
        // 子ノードの候補を作成
        //
        // このノードのモデルの参照プロパティ

        const parentRelationNodes = model.properties.flatMap((property) => {
          const referenceTo = property.referenceTo ?? []
          if (referenceTo.length === 0) {
            return []
          }

          return pipe(
            referenceTo,
            // eslint-disable-next-line max-nested-callbacks
            Array.flatMapNullishOr((referenceToValue) => {
              const referencedModel = modelSearcher.searchModel(referenceToValue.modelName)
              if (referencedModel === undefined) {
                return
              }

              const referenceToProperty: ReferenceToPropertyForAddChildNodeMutation = {
                parentNodeName: node.name,
                nodeName: node.name, // parent側が参照しているということ
                modelName: model.name,
                propertyName: property.name,
                referenceTo: referenceToValue,
              }

              const existedNode = findSameReferenceToChild(node, referenceToProperty)
              return {
                type: 'parent',
                groupLabel: t(`{{label}}の参照項目({{name}})`, { label: nodePathAsLabel.join(': '), name: node.name }),
                // newNodeModel: referencedModel,
                // property,
                value: JSON.stringify([`parent`, node.name, referenceToProperty]),
                key: JSON.stringify([
                  `parent`,
                  node.name,
                  referenceToProperty,
                  ...nodePathAsLabel,
                  model.label,
                  property.label,
                ]),
                // 参照項目の場合、項目名をユーザーに提示してやる仕様
                // label: <span>{referencedModel.label}<span className="text-gray-400 text-sm"> - {referencedModel.label}.{referenceToValue.key ?? 'id'}と{model.label}.{property.label}で紐付け</span></span>,
                label: (
                  <span>
                    {property.label.replace(/ID$/v, '')}
                    <span className="text-sm text-gray-400"> - {referencedModel.name}</span>
                  </span>
                ),
                referenceToProperty,
                node,
                existedNode,
                nodePathAsLabel,
              }
            }),
          )
        })

        // このノードのモデルを参照している、他のモデルの参照プロパティ
        const childRelationNodes = models.flatMap((m) =>
          pipe(
            m.properties,
            // eslint-disable-next-line max-nested-callbacks
            Array.flatMapNullishOr((property) => {
              const referenceTo = property.referenceTo ?? []
              // eslint-disable-next-line max-nested-callbacks,sonarjs/no-nested-functions
              const referenceToValue = referenceTo.find((x) => x.modelName === model.name)
              if (referenceToValue === undefined) {
                return
              }

              const referenceToProperty: ReferenceToPropertyForAddChildNodeMutation = {
                parentNodeName: node.name,
                nodeName: undefined, // 参照しているのはchild側のプロパティ
                modelName: m.name,
                propertyName: property.name,
                referenceTo: referenceToValue,
              }

              const existedNode = findSameReferenceToChild(node, referenceToProperty)
              return {
                type: 'child',
                groupLabel: t(`{{label}}を参照しているモデル({{name}})`, {
                  label: nodePathAsLabel.join(': '),
                  name: node.name,
                }),
                // newNodeModel: m,
                // property,
                value: JSON.stringify([`child`, node.name, referenceToProperty]),
                key: JSON.stringify([
                  `child`,
                  node.name,
                  referenceToProperty,
                  ...nodePathAsLabel,
                  m.label,
                  property.label,
                ]),
                // 参照されている項目は、モデル名(プロパティ名)の形式にする
                label: (
                  <span>
                    {m.label}({property.label.replace(/ID/v, '')})
                    <span className="text-sm text-gray-400"> - {m.name}</span>
                  </span>
                ),
                referenceToProperty,
                node,
                existedNode,
                nodePathAsLabel,
              }
            }),
          ),
        )
        return Array.dedupeWith([...parentRelationNodes, ...childRelationNodes], (a, b) => a.value === b.value)
      }),
    [modelSearcher, models, nodes],
  )
}

export const userModels = new Set([
  'salescore_users',
  'salesforce_user',
  'hubspot_owners',
  'hubspot_users',
  'zoho_user',
  'next_sfa_user_info',
  'comdesk_staff',
  'senses_users',
  'google_sheets_users',
  'microsoft_dynamics_systemusers',
])

export const useCurrentNodeUserReferenceProperties = () => {
  const properties = useCurrentNodeProperties()

  return useMemo(
    () =>
      pipe(
        properties,
        Array.flatMapNullishOr((x) => {
          const { referenceTo } = x.property
          if (referenceTo === undefined) {
            return
          }
          return {
            ...x,
            referenceTo,
          }
        }),
        Array.filter(({ referenceTo }) => referenceTo.some((x) => userModels.has(x.modelName))),
      ),
    [properties],
  )
}

export const usePropertiesForCurrentMeasure = () => {
  const form = useRecoilValue(kpiFormAtom)
  const measure = form.metricFormValue?.measure
  const properites = useCurrentNodeProperties()
  if (measure?.type !== 'kpiPreset') {
    return []
  }
  const presetFunction = presetFunctions().find((x) => x.value === measure.function)
  if (presetFunction === undefined) {
    return []
  }

  const { availableFieldType } = presetFunction
  if (availableFieldType === undefined) {
    return properites
  }
  return properites.filter(({ property }) => availableFieldType.includes(property.type))
}

export const useValidatedFormValue = (): {
  name: string | undefined
  viewGroupId: string | null | undefined
  dashboardIds: string[] | undefined
  kpiConfig: ZodSafeParseResult<ViewConfigKpi>
} => {
  const form = useRecoilValue(kpiFormAtom)
  const { name, viewGroupId, dashboardIds, metricFormValue, ...rest } = form
  const patch = metricFormValueToViewConfigKpiPatch(metricFormValue)
  const kpiConfig = viewConfigKpiSchema.safeParse({
    ...rest,
    ...patch,
    // formとしての型を、configに変換する必要がある
    // schemaが違う時に変換したい時とエラーにしたいときの2パターンがあり、どう処理するか悩ましい
    // 一旦問題になるのが以下のみなので、ここにベタ書きする
    user: patch.user?.joinAs !== undefined && patch.user.property !== undefined ? patch.user : undefined,
  })
  return {
    name,
    viewGroupId,
    dashboardIds,
    kpiConfig: normalizeZodSafeParseResult(kpiConfig),
  }
}

export const useCurrentNodeProperties = () => {
  const currentSheet = useCurrentSheet()
  const nodes = useCurrentNodeModels(currentSheet.tree)
  return nodes.flatMap((node) =>
    node.model.properties.map((property) => ({
      node,
      model: node.model,
      property,
      nodeProeprtyName: {
        nodeName: node.node.name,
        modelName: node.model.name,
        propertyName: property.name,
      },
    })),
  )
}
